import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { apiClient, authService } from '../services'
import toast from 'react-hot-toast'
import { setDocumentTitle, usePageTitle } from '../utils/translation'
import axios from 'axios'

export default function LineCallback() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const [error, setError] = useState<string | null>(null)
  const [processing, setProcessing] = useState<boolean>(true)

  const [lineData, setLineData] = useState<any>(null)

  // Get translated page title
  const pageTitle = usePageTitle('account.lineCallback', 'LINE Authentication')

  useEffect(() => {
    // Set the document title
    setDocumentTitle(pageTitle)

    // Process LINE callback params
    processCallback()
  }, [])

  const processCallback = async () => {
    try {
      setProcessing(true)
      // Get query parameters from URL
      const params = new URLSearchParams(location.search)
      const code = params.get('code')
      const state = params.get('state')
      const error = params.get('error')
      const errorDescription = params.get('error_description')

      // Handle errors from LINE
      if (error) {
        const errorMsg = errorDescription || error || 'Authentication failed'
        console.error('LINE authentication error:', error, errorDescription)
        setError(errorMsg)
        toast.error(errorMsg)
        return
      }

      // Make sure code and state are present
      if (!code || !state) {
        const errorMsg = 'Invalid callback parameters'
        setError(errorMsg)
        toast.error(errorMsg)
        return
      }

      // Verify state to prevent CSRF attacks
      const storedState = localStorage.getItem('line_auth_state')
      if (state !== storedState) {
        const errorMsg = 'Security verification failed'
        setError(errorMsg)
        toast.error(errorMsg)
        return
      }

      if (!lineData) {
        // Exchange the code for a token via backend API
        const response: any = await authService.handleLineCallback(
          code as string,
          state as string
        )

        setLineData(response)
        console.log('LINE CALLBACK RESPONSE', response)

        // Success!
        toast.dismiss()
        toast.success('LINE account connected successfully')
      }
    } catch (error) {
      const errorMsg =
        error instanceof Error
          ? error.message
          : 'Failed to process LINE authentication'
      console.error('Error processing LINE callback:', error)
      setError(errorMsg)
      toast.error(errorMsg)
    } finally {
      // Clean up the state from localStorage
      setProcessing(false)
      localStorage.removeItem('line_auth_state')
    }
  }

  return (
    <main className='min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4'>
      <div className='bg-white rounded-xl shadow p-6 max-w-md w-full'>
        <h1 className='text-xl font-semibold text-center mb-4'>
          {lineData
            ? 'Authentication Successful'
            : processing
            ? 'Processing LINE Authentication...'
            : error
            ? 'Authentication Failed'
            : 'Authentication Failed'}
        </h1>

        {lineData ? (
          <div className='text-center'>
            <p className='text-green-600 mb-4'>
              LINE account connected successfully
            </p>
            <button
              onClick={() => navigate('/me')}
              className='px-6 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors'
            >
              Continue
            </button>
          </div>
        ) : processing ? (
          <div className='flex flex-col items-center justify-center py-6'>
            <div className='animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500 mb-4'></div>
            <p className='text-gray-600 text-center'>
              Connecting your LINE account...
            </p>
          </div>
        ) : (
          <div className='text-center'>
            <p className='text-red-600 mb-4'>{error}</p>
            <button
              onClick={() => navigate('/connect-line')}
              className='px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg transition-colors'
            >
              Try Again
            </button>
          </div>
        )}
      </div>
    </main>
  )
}
